import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { scrollAppToTop } from '../../utils/scroll-app-main';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_LABEL_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_NARROW_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

type ImpersonationActionType = 'started' | 'stopped';

interface ImpersonationAction {
  id: string;
  createdAt: string;
  action: ImpersonationActionType;
  adminUserId: string;
  targetUserId: string;
  adminUserEmail?: string;
  targetUserEmail?: string;
}

const ACTIONS_PER_PAGE = 20;

export const ImpersonationActionsAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [actions, setActions] = useState<ImpersonationAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<ImpersonationActionType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchActions = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/admin/impersonation-actions?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin/impersonation-actions`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch impersonation actions');
        }

        const data = await response.json();
        setActions(data.actions || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching impersonation actions:', err);
        setError(t('failed_to_load_impersonation_actions'));
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getActionLabel = (action: ImpersonationActionType): string => {
    switch (action) {
      case 'started':
        return t('impersonation_action_started');
      case 'stopped':
        return t('impersonation_action_stopped');
      default:
        return action;
    }
  };

  const filteredActions = filterAction === 'all'
    ? actions
    : actions.filter((action) => action.action === filterAction);

  const totalPages = Math.ceil(filteredActions.length / ACTIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * ACTIONS_PER_PAGE;
  const endIndex = startIndex + ACTIONS_PER_PAGE;
  const paginatedActions = filteredActions.slice(startIndex, endIndex);

  const handleFilterChange = (newFilter: ImpersonationActionType | 'all') => {
    setFilterAction(newFilter);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollAppToTop('smooth');
  };

  return (
    <>
      <div className={ADMIN_PAGE_NARROW_CLASSNAME}>
        <div className={`${ADMIN_FILTER_BAR_CLASSNAME} mb-6 justify-start`}>
          <label className={ADMIN_LABEL_CLASSNAME}>
            {t('filter_by_action')}:
          </label>
          <select
            value={filterAction}
            onChange={(e) => handleFilterChange(e.target.value as ImpersonationActionType | 'all')}
            className={ADMIN_INLINE_SELECT_CLASSNAME}
          >
            <option value="all">{t('all')}</option>
            <option value="started">{t('impersonation_action_started')}</option>
            <option value="stopped">{t('impersonation_action_stopped')}</option>
          </select>
          <span className={ADMIN_MUTED_TEXT_CLASSNAME}>
            {t('showing')} {filteredActions.length} {t('impersonation_actions_count')}
          </span>
        </div>

        {error && (
          <div className="mb-4">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        {loading ? (
          <AdminLoadingState />
        ) : filteredActions.length === 0 ? (
          <AdminEmptyState>{t('no_impersonation_actions')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[700px]">
              <thead>
                <tr>
                  <th>
                    {t('date')}
                  </th>
                  <th>
                    {t('action')}
                  </th>
                  <th>
                    {t('admin_user')}
                  </th>
                  <th>
                    {t('impersonated_user')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedActions.map((action) => (
                  <tr key={action.id}>
                    <td>
                      {formatDate(action.createdAt)}
                    </td>
                    <td>
                      {getActionLabel(action.action)}
                    </td>
                    <td>
                      <Link
                        to={`/admin/users/${action.adminUserId}`}
                        className="admin-table-link"
                      >
                        {action.adminUserEmail || action.adminUserId}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/admin/users/${action.targetUserId}`}
                        className="admin-table-link"
                      >
                        {action.targetUserEmail || action.targetUserId}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}

        {!loading && filteredActions.length > 0 && (
          <AdminPaginationBar
            page={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
};
